import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { UserService } from '../../services/user.service';
import { UploadService } from '../../upload/services/upload.service';
import { formatString } from '../../utils/string-utils';

// Custom wrapper extending the File class
class WrappedFile {
	file: File;
	invalid?: boolean;
	sizeStr?: string;
	currentFileSrc?: string;
	ready?: string;

	constructor(file: File) {
		this.file = file;
		this.invalid = false;
		this.sizeStr = '';
		this.currentFileSrc = '';
		this.ready = '';
	}
}

/**
 * Component for loading a new product thumbnail image.
 */
@Component({
	selector: 'thumbnail-loader',
	templateUrl: './thumbnail-loader.component.html',
	styleUrl: './thumbnail-loader.component.css'
})
export class ThumbnailLoaderComponent implements OnInit, OnDestroy {
	@Input() product: any;
	
	private uploadServiceSubscription: Subscription = new Subscription();

	LOCALIZATION: any;

	uploadFiles: WrappedFile[] = [];
	uploadFile: any;
	ingestMsgType = 'info';
	ingestMsg: string = '';
	fallbackImage: string = '/app/aw-ui-angular/assets/img/noimage.webp';
	inprogress: boolean = false;

	constructor(
		private configService: ConfigService,
		private ngbmodalService: NgbActiveModal,
		private userService: UserService,
		private alertsService: AlertsService,
		private uploadService: UploadService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.product._thumbs = formatString(this.configService.getConfig().record.thumbnailURL, this.product.id);

		this.uploadServiceSubscription = this.uploadService.broadcastEvent.subscribe((event: any) => {
			if ("thumbLoadStatus" === event.type) {

				console.log('Thumbnail load status event received!', event);

				if (event.data.ready === "SUCCESS") {
					this.uploadFile.ready = 'SUCCESS';
				} else if (event.data.ready === "FAIL" || event.data.ready === "FAILLICENSE") {
					this.uploadFile.ready = event.data.ready;
					if (event.data.message) {
						this.uploadFile.message = event.data.message;
					}
				} else {
					this.uploadFile.ready = 'PENDING';
				}
			}
		});
	}

	ngOnDestroy() {
		if (this.uploadServiceSubscription) {
			this.uploadServiceSubscription.unsubscribe();
		}
	}

	onFilesSelected(files: File[]) {
		files.forEach(file => {
			console.log('Added file:', file);
			this.addFile(file);
		});
	}

	addFile(file: File): void {
		if (!file) return;

		this.uploadFiles = []; // Resets to allow only a single file
		this.ingestMsgType = "info";
		this.ingestMsg = "";

		let wrappedFile = new WrappedFile(file);
		wrappedFile.invalid = false;
		wrappedFile.sizeStr = this.uploadService.formatBytes(wrappedFile.file.size, 2);
		this.uploadService.retrievePreview(wrappedFile.file).then(
			(response: any) => {
				wrappedFile.currentFileSrc = response;
				this.uploadFiles.push(wrappedFile);
			},
			(err: any) => {
				console.error("Failed to add file", err);
				this.alertsService.error({
					message: "The file is in the wrong format. Upload JPG, PNG, WEBP, GIF, and BMP files only."
				});
				wrappedFile.currentFileSrc = this.fallbackImage;
				wrappedFile.invalid = true;
				this.uploadFiles.push(wrappedFile);
			}
		);
	}

	removeFile(fileIndex: number): void {
		this.uploadFiles.splice(fileIndex, 1);
		this.ingestMsg = "";
		this.ingestMsgType = "info";
	}

	upload(): void {
		if (this.uploadFiles.length != 1) {
			this.alertsService.error({
				message: "Please select a file."
			});
			return;
		}

		this.uploadFile = this.uploadFiles[0];
		if (this.uploadFile.invalid) {
			this.alertsService.error({
				message: "The file is in the wrong format. Upload JPG, PNG, WEBP, GIF, and BMP files only."
			});
			return;
		}

		this.inprogress = true;
		this.alertsService.info({
			title: "File Upload Started",
			message: "Uploading the thumbnail file. Please wait for the process to finish.",
			delay: 5000
		});

		this.ingestMsgType = "info";
		this.ingestMsg = "Uploading thumbnail file...";
		this.uploadService.loadThumbnail(this.uploadFile.file, this.product.id).then(
			(response: any) => {
				this.inprogress = false;
				if (this.uploadFile.ready !== 'SUCCESS') {
					this.ingestMsg = "Failed to upload thumbnail. " + this.uploadFile.message;
				} else {
					this.ingestMsgType = "success";
					this.ingestMsg = "Thumbnail uploaded successfully.";
				}
			}
		);
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
